"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Gitmap, type ContributionDay, type GitmapColors } from "./Gitmap";
import { SnakeOverlay, MatrixOverlay } from "./GitmapEggs";
import { GITMAP_THEMES, DEFAULT_THEME_ID, getTheme } from "@/lib/gitmap-themes";
import { cn } from "@/lib/utils";

type EggMode = "off" | "snake" | "matrix";

const DAY_LABEL_GUTTER = 40;
const WEEKS = 53;
const MIN_CELL = 4;
const MAX_CELL = 18;

interface InitialData {
    days: ContributionDay[];
    from: string;
    to: string;
}

function computeSizing(width: number): { cell: number; gap: number; showDays: boolean } {
    const dayLabel = width < 360 ? 0 : DAY_LABEL_GUTTER;
    const gap = width < 380 ? 1 : width < 480 ? 2 : 3;
    const available = width - dayLabel;
    const totalGap = (WEEKS - 1) * gap;
    const raw = (available - totalGap) / WEEKS;
    const cell = Math.max(MIN_CELL, Math.min(MAX_CELL, Math.floor(raw)));
    return { cell, gap, showDays: dayLabel > 0 };
}

interface Props {
    username: string;
    years: number[];
    initial: InitialData;
}

type Mode = "light" | "dark";

function readMode(): Mode {
    if (typeof document === "undefined") return "light";
    return (document.documentElement.getAttribute("data-theme") as Mode) === "dark"
        ? "dark"
        : "light";
}

function levelsFor(themeId: string, mode: Mode): [string, string, string, string] {
    const t = getTheme(themeId);
    return mode === "dark" ? t.dark : t.light;
}

function buildColors(themeId: string, mode: Mode): GitmapColors {
    const lvls = levelsFor(themeId, mode);
    return {
        empty: "var(--gitmap-empty)",
        level1: lvls[0],
        level2: lvls[1],
        level3: lvls[2],
        level4: lvls[3],
    };
}

export default function GitmapClient({ username, years, initial }: Props) {
    const [year, setYear] = useState<number | null>(null);
    const [themeId, setThemeId] = useState<string>(() => {
        if (typeof window === "undefined") return DEFAULT_THEME_ID;
        const stored = localStorage.getItem("gitmap-theme");
        return stored && GITMAP_THEMES.some((t) => t.id === stored) ? stored : DEFAULT_THEME_ID;
    });
    const [mode, setMode] = useState<Mode>(() => readMode());
    const [data, setData] = useState<InitialData>(initial);
    const [loading, setLoading] = useState(false);
    const [ready, setReady] = useState(false);

    const [yearOpen, setYearOpen] = useState(false);
    const [themeOpen, setThemeOpen] = useState(false);
    const yearRef = useRef<HTMLDivElement>(null);
    const themeRef = useRef<HTMLDivElement>(null);

    const ref = useRef<HTMLDivElement>(null);
    const [cellSize, setCellSize] = useState(10);
    const [cellGap, setCellGap] = useState(3);
    const [showDays, setShowDays] = useState(true);
    const [egg, setEgg] = useState<EggMode>("off");
    const [hovered, setHovered] = useState(false);

    useLayoutEffect(() => {
        const el = ref.current;
        if (el) {
            const sized = computeSizing(el.clientWidth);
            setCellSize(sized.cell);
            setCellGap(sized.gap);
            setShowDays(sized.showDays);
        }

        setReady(true);

        const root = document.documentElement;
        const mo = new MutationObserver(() => setMode(readMode()));
        mo.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
        return () => mo.disconnect();
    }, []);

    useEffect(() => {
        if (!ready) return;
        if (typeof window !== "undefined") localStorage.setItem("gitmap-theme", themeId);
    }, [themeId, ready]);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const ro = new ResizeObserver(() => {
            const sized = computeSizing(el.clientWidth);
            setCellSize(sized.cell);
            setCellGap(sized.gap);
            setShowDays(sized.showDays);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        if (!hovered || egg !== "off") return;
        let buf = "";
        const handler = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            if (
                target &&
                (target.tagName === "INPUT" ||
                    target.tagName === "TEXTAREA" ||
                    target.isContentEditable)
            ) {
                return;
            }
            if (e.key.length !== 1) return;
            buf = (buf + e.key.toLowerCase()).slice(-12);
            if (buf.endsWith("snake")) {
                setEgg("snake");
                buf = "";
            } else if (buf.endsWith("neo") || buf.endsWith("matrix")) {
                setEgg("matrix");
                buf = "";
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [hovered, egg]);

    useEffect(() => {
        if (!themeOpen && !yearOpen) return;
        const onClick = (e: MouseEvent) => {
            const target = e.target as Node;
            if (themeOpen && themeRef.current && !themeRef.current.contains(target)) {
                setThemeOpen(false);
            }
            if (yearOpen && yearRef.current && !yearRef.current.contains(target)) {
                setYearOpen(false);
            }
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [themeOpen, yearOpen]);

    async function pickYear(y: number | null) {
        setYear(y);
        setYearOpen(false);
        setLoading(true);
        const url = new URL("/api/contributions", window.location.origin);
        url.searchParams.set("user", username);
        if (y) url.searchParams.set("year", String(y));
        try {
            const res = await fetch(url.toString());
            if (res.ok) {
                setData((await res.json()) as InitialData);
            }
        } finally {
            setLoading(false);
        }
    }

    const colors = buildColors(themeId, mode);
    const theme = getTheme(themeId);
    const themeLevels = levelsFor(themeId, mode);

    const yearLabel = year === null ? "Last year" : String(year);

    return (
        <div
            className={cn("space-y-3 transition-opacity duration-200", !ready && "opacity-0")}
        >
            <div className="flex items-center justify-between gap-2">
                <div ref={yearRef} className="relative">
                    <button
                        type="button"
                        onClick={() => setYearOpen((v) => !v)}
                        className="inline-flex items-center gap-2 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                        aria-haspopup="listbox"
                        aria-expanded={yearOpen}
                    >
                        <span className="font-mono">{yearLabel}</span>
                        <span aria-hidden className="text-muted-foreground/60">▾</span>
                    </button>
                    <div
                        role="listbox"
                        inert={!yearOpen}
                        className={cn(
                            "absolute left-0 top-full z-20 mt-1 max-h-80 w-36 origin-top-left overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md transition duration-150 ease-out",
                            yearOpen
                                ? "scale-100 opacity-100"
                                : "pointer-events-none scale-95 opacity-0",
                        )}
                    >
                            <button
                                type="button"
                                role="option"
                                aria-selected={year === null}
                                onClick={() => pickYear(null)}
                                className={cn(
                                    "flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-xs transition-colors",
                                    year === null
                                        ? "bg-accent text-foreground"
                                        : "text-muted-foreground hover:bg-accent hover:text-foreground",
                                )}
                            >
                                <span>Last year</span>
                                {year === null && <span aria-hidden>✓</span>}
                            </button>
                            {years.map((y) => (
                                <button
                                    key={y}
                                    type="button"
                                    role="option"
                                    aria-selected={y === year}
                                    onClick={() => pickYear(y)}
                                    className={cn(
                                        "flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-xs font-mono transition-colors",
                                        y === year
                                            ? "bg-accent text-foreground"
                                            : "text-muted-foreground hover:bg-accent hover:text-foreground",
                                    )}
                                >
                                    <span>{y}</span>
                                    {y === year && <span aria-hidden>✓</span>}
                                </button>
                            ))}
                    </div>
                </div>

                <div ref={themeRef} className="relative">
                    <button
                        type="button"
                        onClick={() => setThemeOpen((v) => !v)}
                        className="inline-flex items-center gap-2 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                        aria-haspopup="listbox"
                        aria-expanded={themeOpen}
                    >
                        <span className="inline-flex gap-0.5">
                            {themeLevels.map((c, i) => (
                                <span
                                    key={i}
                                    className="block h-2 w-2 rounded-full"
                                    style={{ backgroundColor: c }}
                                />
                            ))}
                        </span>
                        <span>{theme.name}</span>
                        <span aria-hidden className="text-muted-foreground/60">▾</span>
                    </button>
                    <div
                        role="listbox"
                        inert={!themeOpen}
                        className={cn(
                            "absolute right-0 top-full z-20 mt-1 max-h-80 w-44 origin-top-right overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md transition duration-150 ease-out",
                            themeOpen
                                ? "scale-100 opacity-100"
                                : "pointer-events-none scale-95 opacity-0",
                        )}
                    >
                            {GITMAP_THEMES.map((t) => {
                                const lvls = mode === "dark" ? t.dark : t.light;
                                return (
                                    <button
                                        key={t.id}
                                        type="button"
                                        role="option"
                                        aria-selected={t.id === themeId}
                                        onClick={() => {
                                            setThemeId(t.id);
                                            setThemeOpen(false);
                                        }}
                                        className={cn(
                                            "flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-xs transition-colors",
                                            t.id === themeId
                                                ? "bg-accent text-foreground"
                                                : "text-muted-foreground hover:bg-accent hover:text-foreground",
                                        )}
                                    >
                                        <span className="inline-flex gap-1">
                                            {lvls.map((c, i) => (
                                                <span
                                                    key={i}
                                                    className="block h-2.5 w-2.5 rounded-full"
                                                    style={{ backgroundColor: c }}
                                                />
                                            ))}
                                        </span>
                                        <span className="flex-1">{t.name}</span>
                                        {t.id === themeId && <span aria-hidden>✓</span>}
                                    </button>
                                );
                            })}
                    </div>
                </div>
            </div>

            <div
                ref={ref}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                className={cn("w-full overflow-x-clip transition-opacity duration-200", loading && "opacity-50")}
                style={{ minHeight: 7 * cellSize + 6 * cellGap + 15 }}
            >
                {ready && (
                    <div key={egg} className="animate-egg-in">
                        {egg === "off" && (
                            <Gitmap
                                contributions={data.days}
                                from={new Date(data.from)}
                                to={new Date(data.to)}
                                colors={colors}
                                cellSize={cellSize}
                                cellGap={cellGap}
                                showDays={showDays}
                            />
                        )}
                        {egg === "snake" && (
                            <SnakeOverlay
                                cellSize={cellSize}
                                cellGap={cellGap}
                                colors={colors}
                                onExit={() => setEgg("off")}
                            />
                        )}
                        {egg === "matrix" && (
                            <MatrixOverlay
                                cellSize={cellSize}
                                cellGap={cellGap}
                                colors={colors}
                                onExit={() => setEgg("off")}
                            />
                        )}
                    </div>
                )}
            </div>

            <div className="flex items-center justify-end gap-2 text-[11px] text-muted-foreground">
                <span>Less</span>
                <div className="flex items-center gap-1">
                    <span
                        className="block h-2.5 w-2.5 rounded-[2px]"
                        style={{ backgroundColor: "var(--gitmap-empty)" }}
                    />
                    {themeLevels.map((c, i) => (
                        <span
                            key={i}
                            className="block h-2.5 w-2.5 rounded-[2px]"
                            style={{ backgroundColor: c }}
                        />
                    ))}
                </div>
                <span>More</span>
            </div>
        </div>
    );
}
